/**
 * Identity decision memory (B2, 2026-09-24): a ruling on a name a filing uses
 * for one of our entities. Asked once — the identity check consults it on
 * every later filing, and a same-company ruling re-verifies the entity's held
 * articles (the backend fans out and releases any left with no blocker).
 */
import { apiClient } from './config.js';

export type NameRulingKind = 'same_entity' | 'different_entity' | 'rename_pending';

export interface NameRuling {
  ruling_id: string;
  observed_name: string;
  ruling: NameRulingKind;
  rationale: string | null;
  decided_by: string;
  decided_at: string;
  source: 'operator' | 'cynapse_alias';
}

export interface ArticleNameContext {
  entity_id: string;
  display_name: string;
  code: string | null;
  /** How the filing names itself — what the identity check compared. */
  filing_names: string[];
  live_ruling: NameRuling | null;
}

export interface NameRulingOutcome {
  ruling: NameRuling;
  fanout: { considered: number; refreshed: number; released: number } | null;
  /** The article ruled from, re-verified whatever its age (the fan-out only
   *  covers holds up to 7 days old). Null when the fan-out already covered it. */
  article: {
    verdict: string;
    previous_verdict: string;
    cleared: string[];
    released: { queued: boolean; reason?: string } | null;
  } | null;
}

export async function getArticleNameContext(articleId: string): Promise<ArticleNameContext> {
  const { data } = await apiClient.get<ArticleNameContext>(
    `/admin/entities/by-article/${articleId}/name-context`,
  );
  return data;
}

export async function recordNameRuling(
  entityId: string,
  body: { observed_name: string; ruling: NameRulingKind; rationale?: string; article_id?: string },
): Promise<NameRulingOutcome> {
  const { data } = await apiClient.post<NameRulingOutcome>(
    `/admin/entities/${entityId}/name-rulings`,
    body,
  );
  return data;
}
